import { CCol, CFormLabel, CRow } from '@coreui/react-pro'
import { CColProps } from '@coreui/react-pro/dist/components/grid/CCol'
import React, { useState, useEffect } from 'react'
import Autocomplete from 'react-autocomplete'
import {
  AutoCompleteProps,
  GetAutoCompleteList,
  GetOnSelect,
} from '../../types/ProjectManagement/Project/AddProject/AddProjectTypes'
import {
  booleanMandatory,
  conditionalDefaultStringEmpty,
  StringEmpty,
} from '../../utils/helper'

const OAutoComplete = ({
  dynamicFormLabelProps,
  list,
  onSelect,
  shouldReset,
  value,
  label,
  placeholder,
  name,
}: AutoCompleteProps): JSX.Element => {
  const [autoCompleteTarget, setAutoCompleteTarget] = useState<string>()

  const initList = [] as GetAutoCompleteList[]
  const [selectorList, setSelectorList] = useState(initList)

  useEffect(() => {
    setAutoCompleteTarget(value)
  }, [value])

  useEffect(() => {
    if (list == null) return

    setSelectorList(list)
  }, [list])

  useEffect(() => {
    if (shouldReset) setAutoCompleteTarget('')
  }, [shouldReset])

  const onHandleSelect = (selectedName: string) => {
    if (selectedName?.trim() !== '') {
      setAutoCompleteTarget(selectedName)
    }
    const detail = selectorList?.find(
      (listValue) => listValue?.name === selectedName,
    )

    const user = {
      id: detail?.id,
      name: detail?.name,
    } as GetOnSelect
    onSelect(user)
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoCompleteTarget(e.target.value)
    if (e.target.value === '') onSelect({} as GetOnSelect)
  }

  return (
    <>
      <CRow className="mb-3">
        <CFormLabel
          data-testid={name}
          {...dynamicFormLabelProps(
            `${name}`,
            'col-sm-3 col-form-label text-md-end',
          )}
        >
          {label} :
          {autoCompleteTarget === undefined ||
          autoCompleteTarget?.trim() === '' ? (
            <span className="text-danger">*</span>
          ) : (
            <span className="text-white">*</span>
          )}
        </CFormLabel>
        <CCol sm={3}>
          <Autocomplete
            inputProps={{
              className: 'form-control form-control-sm',
              placeholder: `${placeholder}`,
            }}
            getItemValue={(item) => item?.name}
            items={selectorList}
            data-testid={name}
            wrapperStyle={{ position: 'relative' }}
            renderMenu={(children) => (
              <div
                className={
                  autoCompleteTarget && autoCompleteTarget?.length > 0
                    ? 'autocomplete-dropdown-wrap'
                    : 'autocomplete-dropdown-wrap hide'
                }
              >
                {children}
              </div>
            )}
            renderItem={(item, isHighlighted) => (
              <div
                data-testid="option"
                className={
                  isHighlighted
                    ? 'autocomplete-dropdown-item active'
                    : 'autocomplete-dropdown-item '
                }
                key={item?.id}
              >
                {item?.name}
              </div>
            )}
            value={autoCompleteTarget}
            shouldItemRender={(item, itemValue) =>
              item.name?.toLowerCase().indexOf(itemValue?.toLowerCase()) > -1
            }
            onChange={(e) => onChangeHandler(e)}
            onSelect={(selectedVal) => onHandleSelect(selectedVal)}
          />
        </CCol>
      </CRow>
    </>
  )
}

export const OAutoCompleteDynamic = <T,>(props: {
  items: T[]
  label: string
  name: string // its the name, id, data-testid of the select
  value?: string
  setValueHandler?: (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => void
  isRequired?: boolean
  placeholder?: string
  autocomplete?: 'on' | 'off'
  wrapperStyle?: React.CSSProperties
  getItemValueHandler?(item: T): string
  labelClassName?: string
  selectClassName?: string
  labelColumnType?: CColProps
  selectColumnType?: CColProps
  // eslint-disable-next-line
  shouldItemRender?(item: any, value: string): boolean
  // eslint-disable-next-line
  onSelect?(value: string, item: any): void
}): JSX.Element => {
  const selectedValue = props?.value == null ? '' : props?.value
  const requiredLabel = props?.isRequired ?? false
  const labelColumnTypeValue = props?.labelColumnType ?? {
    sm: { span: 3, offset: 0 },
    lg: { span: 3, offset: 0 },
  }
  const selectColumnType = props?.selectColumnType ?? {
    sm: { span: 6, offset: 0 },
  }
  return (
    <CRow>
      <CCol {...labelColumnTypeValue} className="text-md-end">
        <CFormLabel
          className={`${props?.labelClassName} col-form-label`}
          data-testid={`selectLabel-${props.name}`}
        >
          {props?.label ? `${props?.label}: ` : ''}
          {requiredLabel && (
            <span className={booleanMandatory(StringEmpty(selectedValue))}>
              *
            </span>
          )}
        </CFormLabel>
      </CCol>
      <CCol {...selectColumnType}>
        <div data-testid={`formSelect-${props.name}`}>
          <Autocomplete
            inputProps={{
              id: props.name,
              name: props.name,
              className: conditionalDefaultStringEmpty(
                props?.labelClassName,
                'form-control form-control-sm',
              ),
              autoComplete: props?.autocomplete ?? 'off',
              placeholder: conditionalDefaultStringEmpty(
                props?.placeholder,
                '',
              ),
            }}
            wrapperStyle={{ position: 'relative', ...props?.wrapperStyle }}
            items={props.items}
            getItemValue={
              props?.getItemValueHandler ??
              ((item) => item.firstName + ' ' + item.lastName)
            }
            value={selectedValue?.replace(/^\s*/, '')}
            renderMenu={(children) => (
              <div
                className={
                  !StringEmpty(selectedValue)
                    ? 'autocomplete-dropdown-wrap'
                    : 'autocomplete-dropdown-wrap hide'
                }
              >
                {children}
              </div>
            )}
            renderItem={(item, isHighlighted) => (
              <div
                className={
                  isHighlighted
                    ? 'autocomplete-dropdown-item active'
                    : 'autocomplete-dropdown-item'
                }
                key={item.id}
              >
                {item.firstName + ' ' + item.lastName}
              </div>
            )}
            shouldItemRender={
              props?.shouldItemRender ??
              ((item, value) =>
                item?.firstName?.toLowerCase().indexOf(value.toLowerCase()) >
                -1)
            }
            onChange={props?.setValueHandler}
            onSelect={props?.onSelect}
          />
        </div>
      </CCol>
    </CRow>
  )
}

export default OAutoComplete
